<script setup>
import { useAppStore } from '@/stores/useAppStore';
import api from '@/../lib/api';
import { ref } from 'vue';
import { useStylesStore } from '@/stores/useStylesStore';
import InputWithLabel from '@/../lib/components/commonComponents/InputWithLabel/InputWithLabel.vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n({ useScope: 'global' });

const appStore = useAppStore();
const stylesStore = useStylesStore();

const fileInput = ref(null);
const files = ref([]);
const filesToRender = ref([]);
const isLoading = ref(false);
const styleName = ref(t('stylesModal.defaultStyleName'));

const onClose = () => {
  appStore.showModalStylesVisible(false);
};

const onSendPictures = async () => {
  if (files.value.length === 0) {
    return;
  }

  isLoading.value = true;

  try {
    await api.ai.createNewStyle({
      name: styleName.value,
      photos: [...filesToRender.value],
    });

    stylesStore.getStyles();

    files.value = [];
    filesToRender.value = [];
    styleName.value = t('stylesModal.defaultStyleName');

    appStore.showModalStylesVisible(false);
    appStore.showModalStylesAddedVisible(true);
  } catch (error) {
    // Error toast handled by axios interceptor
  } finally {
    isLoading.value = false;
  }
};

const triggerFileInput = () => {
  if (files.value.length < 4) {
    fileInput.value.click();
  }
};

const prepareFilesAndAdd = (files) => {
  files.forEach((file) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (e) => {
      filesToRender.value = [...filesToRender.value, e.target.result];
    };
  });
};

const handleFiles = (e) => {
  const newFiles = Array.from(e.target.files);
  const remainingSlots = 4 - files.value.length;

  if (remainingSlots > 0) {
    const filesToAdd = newFiles.slice(0, remainingSlots);

    prepareFilesAndAdd(filesToAdd);
    files.value = [...files.value, ...filesToAdd];
  }
};

const handleDrop = (e) => {
  e.preventDefault();
  if (files.value.length >= 4) return;

  const droppedFiles = Array.from(e.dataTransfer.files);
  const remainingSlots = 4 - files.value.length;

  if (droppedFiles.length > 0 && remainingSlots > 0) {
    const filesToAdd = droppedFiles.slice(0, remainingSlots);

    prepareFilesAndAdd(filesToAdd);

    files.value = [...files.value, ...filesToAdd];
  }
};

const removeFile = (index) => {
  files.value.splice(index, 1);
  filesToRender.value.splice(index, 1);
};
</script>

<template>
  <AppModal v-model="appStore.isModalStylesVisible" :close="onClose">
    <div class="modal">
      <button class="modal_close" @click="onClose"><span /></button>
      <h3 class="modal--title">{{ $t('stylesModal.uploadYourBanner') }}</h3>
      <span class="modal--description">{{ $t('stylesModal.upTo4') }}</span>
      <div class="modal--content">
        <InputWithLabel
          v-model="styleName"
          :label="$t('stylesModal.styleName')"
        />
        <div
          class="modal--dropzone"
          @click="triggerFileInput"
          @dragover.prevent
          @drop.prevent="handleDrop"
        >
          <AppIcon icon="plus" :size="15" />
          <input
            type="file"
            ref="fileInput"
            multiple
            accept="image/*"
            @change="handleFiles"
            style="display: none"
          />
        </div>
        <div class="modal--imagesGrid">
          <div
            v-for="(image, index) in filesToRender"
            :key="index"
            class="modal--imagesItem"
          >
            <div
              class="modal--imagesPreview"
              :style="`background-image: url('${image}');`"
            />
            <div
              class="modal--imagesDeleteBtn"
              @click="() => removeFile(index)"
            >
              <AppIcon icon="bucket" :size="10" />
            </div>
          </div>
        </div>
        <div class="modal--actions">
          <AppButton color="black" blank @click="onClose">
            {{ $t('userEditor.back') }}
          </AppButton>
          <AppButton
            color="primary"
            spinner
            :loading="isLoading"
            :disabled="files.length === 0"
            @click="onSendPictures"
          >
            {{ $t('authModal.save') }}
          </AppButton>
        </div>
      </div>
    </div>
  </AppModal>
</template>

<style lang="scss" scoped>
.modal {
  padding: 19px;

  &--content {
    margin-top: 32px;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &--title {
    margin-bottom: 0;
  }

  &--description {
    display: block;
    width: 100%;
    text-align: center;
    color: #989898;
  }

  &--dropzone {
    width: 100%;
    height: 150px;
    display: grid;
    justify-content: center;
    align-items: center;
    background: rgba(36, 103, 236, 0.11);
    border: dashed 1px #2467ec;
    border-radius: 9px;
    color: #2467ec;
  }

  &--imagesGrid {
    margin-top: 11px;
    display: grid;
    grid-template-rows: repeat(auto-fill, 55px);
    grid-template-columns: repeat(auto-fill, 55px);
    gap: 4px;
  }

  &--imagesItem {
    position: relative;
    width: 55px;
    height: 55px;
  }

  &--imagesPreview {
    width: 100%;
    height: 100%;
    background-position: center;
    background-size: cover;
    border-radius: 9px;
  }

  &--imagesDeleteBtn {
    position: absolute;
    top: -4px;
    right: -8px;
    width: 17px;
    height: 17px;
    display: grid;
    justify-content: center;
    align-items: center;
    background-color: #fff;
    color: #ee5151;
    border-radius: 50%;
    cursor: pointer;
    z-index: 1;
  }

  &--actions {
    margin-top: 20px;
    display: grid;
    grid-template-columns: repeat(2, 114px);
    justify-content: end;
  }
}
</style>
